import { Fragment } from 'react';
import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Url from 'urlinator/Url';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { getLongDisplayName } from 'MeetingsLib/utils/utils';
import ProfileHeader from 'MeetingsLib/components/header/ProfileHeader';
import FreemiumUpgradeButton from 'MeetingsLib/components/freemium/FreemiumUpgradeButton';
import BookingStepIndicator from 'MeetingsLib/components/sections/BookingStepIndicator';
import { track } from 'MeetingsLib/tracking/UsageTracker';
import getFreemiumViralLink from 'MeetingsLib/components/freemium/getFreemiumViralLink';
import { THANKYOU_VIRAL_HUBS_CTA } from 'MeetingsLib/constants/paths';
import { BOOKING_STEPS_END } from 'MeetingsLib/constants/steps';
import { redirectToUrl } from 'MeetingsLib/utils/utils';
import { SECOND, REDIRECT_DELAY_IN_SECONDS } from 'MeetingsLib/constants/time';
import { H4 } from 'foundations-components/transitional/components/H4';
import { useCallback, useContext, useEffect, useState } from 'react';
import PreviewContext from '../../utils/PreviewContext';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { View } from 'foundations-components/components/View';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function Advert({
  branding,
  embedded,
  isSalesStarter,
  portalId
}) {
  const goToMeetingsPage = useCallback(() => {
    track('meetingsLibClickPromoLink', {
      page: 'thank you'
    });
    const thankYouViralLink = getFreemiumViralLink(THANKYOU_VIRAL_HUBS_CTA, portalId, embedded, true);
    window.open(thankYouViralLink, '_blank', 'noopener nofollow');
  }, [portalId, embedded]);
  if (isSalesStarter) {
    return null;
  }
  return /*#__PURE__*/_jsx(TFlex, {
    align: "center",
    justify: "center",
    children: /*#__PURE__*/_jsx(FreemiumUpgradeButton, {
      handleButtonClick: () => goToMeetingsPage(),
      branding: branding,
      portalId: portalId,
      embedded: embedded
    })
  });
}
function RedirectCountdown({
  timeTillRedirect,
  willRedirect
}) {
  if (willRedirect) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      layout: "centered",
      showLabel: true,
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "summary.redirect",
        options: {
          seconds: timeTillRedirect
        }
      }),
      paddingBlockStart: "400"
    });
  }
  return null;
}
export default function BookingSummary({
  bookedDateTime,
  branding,
  embedded,
  isSalesStarter,
  offline,
  profile,
  userTimezone,
  onSuccess,
  redirectUrl,
  allowRedirect,
  portalId,
  compact,
  isPaidMeeting,
  isPreviewed,
  paidMeetingInfo,
  paidMeetingPrice
}) {
  const isPreview = useContext(PreviewContext);
  const url = new Url(window.location);
  const embeddedInChatbot = url.paramValue('embeddedInChatbot') === 'true';
  const [iframeWidth, setIframeWidth] = useState(document.documentElement.clientWidth);
  useEffect(() => {
    if (!embeddedInChatbot) {
      return;
    }
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.target === document.documentElement) {
          setIframeWidth(entry.contentRect.width);
        }
      }
    });
    observer.observe(document.documentElement);
    return () => observer.disconnect();
  }, [embeddedInChatbot]);
  const [timeTillRedirect, setTimeTillRedirect] = useState(REDIRECT_DELAY_IN_SECONDS);
  const organizerEmail = profile.email;
  const organizerTemporarilyUnavailable = organizerEmail === '';
  const willRedirect = redirectUrl && isSalesStarter && allowRedirect;
  const updateTimeTillRedirect = useCallback(() => {
    setTimeTillRedirect(_timeTillRedirect => _timeTillRedirect > 1 ? _timeTillRedirect - 1 : 1);
  }, [setTimeTillRedirect]);
  const redirectToCustomPage = useCallback(() => {
    redirectToUrl(redirectUrl, embedded);
  }, [redirectUrl, embedded]);
  useEffect(() => {
    let redirectInterval;
    let redirectTimeout;
    onSuccess();

    // check isPreview here because we want to show the redirect UI in the
    // preview, but not actually start any timers
    if (willRedirect && !isPreview) {
      redirectInterval = window.setInterval(updateTimeTillRedirect, SECOND);
      redirectTimeout = window.setTimeout(redirectToCustomPage, REDIRECT_DELAY_IN_SECONDS * SECOND);
    }
    return () => {
      if (redirectTimeout) {
        window.clearTimeout(redirectTimeout);
      }
      if (redirectInterval) {
        window.clearInterval(redirectInterval);
      }
    };
  }, [onSuccess, willRedirect, isPreview, updateTimeTillRedirect, redirectToCustomPage]);
  const shouldRenderDateTime = !willRedirect;
  let suffix = offline ? 'offline' : 'online';
  suffix = offline && organizerTemporarilyUnavailable ? 'offline_temp_unavailable' : suffix;
  const redirect = willRedirect ? 'redirect.' : '';
  const preMessageKey = `summary.tagline.${redirect}${suffix}`;
  const renderConfirmTagline = () => {
    if (isPaidMeeting) {
      const paidPreMessageKey = `${preMessageKey}_paid`;
      const currencyAndAmount = /*#__PURE__*/_jsx(FormattedIntlCurrency, {
        value: paidMeetingPrice,
        currencyCode: paidMeetingInfo.currency
      });
      return /*#__PURE__*/_jsx(FormattedReactMessage, {
        "data-selenium": "paid-meeting-success-summary",
        "data-selenium-amount": paidMeetingPrice,
        message: paidPreMessageKey,
        options: {
          amount: currencyAndAmount,
          fullName: getLongDisplayName(profile)
        }
      });
    } else {
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: preMessageKey,
        options: {
          fullName: getLongDisplayName(profile)
        }
      });
    }
  };
  return /*#__PURE__*/_jsxs(Fragment, {
    "data-selenium-test": `booked-dateTime-${bookedDateTime}`,
    children: [/*#__PURE__*/_jsx(BookingStepIndicator, {
      compact: compact,
      currStep: BOOKING_STEPS_END.SUMMARY,
      embedded: embedded,
      isPreviewed: isPreviewed,
      withPayment: isPaidMeeting
    }), /*#__PURE__*/_jsx(View, {
      inlineSize: embeddedInChatbot ? `${iframeWidth}px` : '400px',
      marginInline: "auto",
      paddingBlockStart: !embedded ? '1200' : undefined,
      "data-test-id": "book-summary",
      children: /*#__PURE__*/_jsx(CardWrapper, {
        padding: "0",
        height: "100%",
        "data-timestamp": bookedDateTime,
        "data-selenium-test": "booking-summary-step",
        children: /*#__PURE__*/_jsx(CardSection, {
          padding: "0",
          children: /*#__PURE__*/_jsxs(View, {
            marginInline: "400",
            paddingInline: "300",
            paddingBlock: "1100",
            textAlign: "center",
            children: [/*#__PURE__*/_jsx(ProfileHeader, {
              booked: true,
              offline: offline,
              profile: profile
            }), /*#__PURE__*/_jsx(Paragraph, {
              textAlign: "center",
              marginBlockStart: "300",
              "data-selenium-test": `booked-dateTime-${bookedDateTime}`,
              children: renderConfirmTagline()
            }), shouldRenderDateTime && /*#__PURE__*/_jsxs(H4, {
              "data-selenium-test": `booked-dateTime-${bookedDateTime}`,
              marginBlockEnd: "100",
              marginBlockStart: "500",
              children: [I18n.moment(bookedDateTime).tz(userTimezone).format('LL'), /*#__PURE__*/_jsx("br", {}), I18n.moment(bookedDateTime).tz(userTimezone).format('LT')]
            }), /*#__PURE__*/_jsx(Advert, {
              portalId: portalId,
              embedded: embedded,
              branding: branding,
              isSalesStarter: isSalesStarter
            }), /*#__PURE__*/_jsx(RedirectCountdown
            // @ts-ignore fix type
            , {
              willRedirect: willRedirect,
              timeTillRedirect: timeTillRedirect
            })]
          })
        })
      })
    })]
  });
}